import React, { useState, useEffect } from 'react';
import { X, Target } from 'lucide-react';
import CampaignForm from '../forms/CampaignForm';

/**
 * CampaignModal - Modal for creating/editing campaigns
 */
const CampaignModal = ({
    isOpen,
    onClose,
    onSave,
    initialData = null,
    mode = 'create' // 'create' or 'edit'
}) => {
    const [formData, setFormData] = useState({
        name: '',
        objective: 'AWARENESS',
        platform: 'meta',
        budget: { amount: 0, type: 'daily' },
        funnelStage: 'TOFU',
        schedule: { start: '', end: '' }
    });

    const [errors, setErrors] = useState({});

    // Initialize form with initialData when editing
    useEffect(() => {
        if (initialData && mode === 'edit') {
            setFormData({
                name: initialData.name || '',
                objective: initialData.objective || 'AWARENESS',
                platform: initialData.platform || 'meta',
                budget: initialData.budget || { amount: 0, type: 'daily' },
                funnelStage: initialData.funnelStage || 'TOFU',
                schedule: initialData.schedule || { start: '', end: '' }
            });
        } else {
            // Reset form for create mode
            setFormData({
                name: '',
                objective: 'AWARENESS',
                platform: 'meta',
                budget: { amount: 0, type: 'daily' },
                funnelStage: 'TOFU',
                schedule: { start: '', end: '' }
            });
        }
        setErrors({});
    }, [initialData, mode, isOpen]);

    // Auto-suggest funnel stage based on objective
    useEffect(() => {
        const stageMap = {
            AWARENESS: 'TOFU',
            TRAFFIC: 'TOFU',
            ENGAGEMENT: 'MOFU',
            LEADS: 'MOFU',
            CONVERSIONS: 'BOFU',
            SALES: 'BOFU'
        };
        if (!initialData) {
            setFormData(prev => ({
                ...prev,
                funnelStage: stageMap[prev.objective] || 'TOFU'
            }));
        }
    }, [formData.objective, initialData]);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleBudgetChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            budget: { ...prev.budget, [field]: value }
        }));
    };

    const handleScheduleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            schedule: { ...prev.schedule, [field]: value }
        }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Campaign name is required';
        }
        if (formData.budget.amount <= 0) {
            newErrors.budget = 'Budget must be greater than 0';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;

        onSave({
            ...formData,
            id: initialData?.id || `campaign_${Date.now()}`
        });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            className="modal-backdrop"
            onClick={onClose}
            style={{ zIndex: 10000 }}
        >
            <div
                className="modal"
                onClick={(e) => e.stopPropagation()}
                style={{
                    maxWidth: '600px',
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}
            >
                {/* Header */}
                <div className="modal-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: 'var(--radius-md)',
                            background: 'var(--color-primary-light)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Target size={20} style={{ color: 'var(--color-primary)' }} />
                        </div>
                        <div>
                            <h2 className="modal-title">
                                {mode === 'edit' ? 'Edit Campaign' : 'Create Campaign'}
                            </h2>
                            <p style={{
                                margin: 0,
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                Configure your campaign settings
                            </p>
                        </div>
                    </div>
                    <button className="btn btn-icon btn-ghost" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <CampaignForm
                            formData={formData}
                            onChange={handleChange}
                            errors={errors}
                        />
                    </div>

                    {/* Footer */}
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                        >
                            {mode === 'edit' ? 'Save Changes' : 'Create Campaign'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CampaignModal;
